/**
 * Job Entity
 * Represents an async processing job from the ModernizeIT API
 */

export const JobType = {
  INGEST: 'ingest',
  CODE_ANALYSIS: 'codeanalysis',
  CODE_REFACTOR: 'coderefactor',
  DEPENDENCY_MAPPER: 'dependencymapper',
  MONOLITH_IDENTIFIER: 'monolithidentifier',
  DATA_ANALYSIS: 'dataanalysis',
  DISCOVERY: 'discovery',
  ARCHITECTURE: 'architecture',
  SERVICE_GENERATION: 'servicegeneration',
}

export const JobStatus = {
  PENDING: 'pending',
  RUNNING: 'running',
  COMPLETED: 'completed',
  FAILED: 'failed',
}

export class Job {
  constructor({
    id,
    type,
    status = JobStatus.PENDING,
    applicationId,
    scoutAccountId,
    applicationName,
    progress = 0,
    message = '',
    results = null,
    artifacts = [],
    error = null,
    createdAt = new Date(),
    startedAt = null,
    completedAt = null,
  }) {
    this.id = id
    this.type = type
    this.status = status
    this.applicationId = applicationId
    this.scoutAccountId = scoutAccountId
    this.applicationName = applicationName
    this.progress = progress
    this.message = message
    this.results = results
    this.artifacts = artifacts
    this.error = error
    this.createdAt = createdAt instanceof Date ? createdAt : new Date(createdAt)
    this.startedAt = startedAt ? (startedAt instanceof Date ? startedAt : new Date(startedAt)) : null
    this.completedAt = completedAt ? (completedAt instanceof Date ? completedAt : new Date(completedAt)) : null
  }

  isComplete() {
    return this.status === JobStatus.COMPLETED || this.status === JobStatus.FAILED
  }

  isSuccess() {
    return this.status === JobStatus.COMPLETED
  }

  isRunning() {
    return this.status === JobStatus.RUNNING
  }

  isPending() {
    return this.status === JobStatus.PENDING
  }

  isFailed() {
    return this.status === JobStatus.FAILED
  }

  /**
   * Get the API endpoint path for this job type
   */
  get apiEndpoint() {
    const endpoints = {
      [JobType.INGEST]: 'ingest',
      [JobType.CODE_ANALYSIS]: 'codeanalysis',
      [JobType.CODE_REFACTOR]: 'coderefactor',
      [JobType.DEPENDENCY_MAPPER]: 'dependencymapper',
      [JobType.MONOLITH_IDENTIFIER]: 'monolithidentifier',
      [JobType.DATA_ANALYSIS]: 'dataanalysis',
      [JobType.DISCOVERY]: 'discovery',
      [JobType.ARCHITECTURE]: 'architecture',
      [JobType.SERVICE_GENERATION]: 'servicegeneration',
    }
    return endpoints[this.type] || this.type
  }

  static create(data) {
    return new Job({
      id: data.id,
      type: data.type,
      status: JobStatus.PENDING,
      applicationId: data.applicationId,
      scoutAccountId: data.scoutAccountId,
      applicationName: data.applicationName,
      createdAt: new Date(),
    })
  }

  updateStatus(status, message = '', results = null) {
    this.status = status
    this.message = message
    if (results) this.results = results

    if (status === JobStatus.RUNNING && !this.startedAt) {
      this.startedAt = new Date()
    }
    if (status === JobStatus.COMPLETED || status === JobStatus.FAILED) {
      this.completedAt = new Date()
    }
  }

  toJSON() {
    return {
      id: this.id,
      type: this.type,
      status: this.status,
      applicationId: this.applicationId,
      scoutAccountId: this.scoutAccountId,
      applicationName: this.applicationName,
      progress: this.progress,
      message: this.message,
      results: this.results,
      artifacts: this.artifacts,
      error: this.error,
      createdAt: this.createdAt.toISOString(),
      startedAt: this.startedAt?.toISOString() || null,
      completedAt: this.completedAt?.toISOString() || null,
    }
  }
}
